
import React from 'react';
import { Building2, Users, MapPin, Layers3, Home, UserCircle } from 'lucide-react';
import { BooleanIcon } from "@nodus/ui";
import { Avatar, Button, Card, Text } from '@mantine/core';
import { Customer } from '@/lib/api/types';
import StatItem from '@/components/stats/StatItem';

interface CustomerCardProps {
  customer: Customer;
  onSelect: (customer: Customer) => void;
  isLoading?: boolean;
  showStats?: boolean;
  isLastActive?: boolean;
}

const CustomerCard: React.FC<CustomerCardProps> = ({
  customer,
  onSelect,
  isLoading = false,
  showStats = true,
  isLastActive = false
}) => {
  const shouldShowStats = showStats && customer.stats !== undefined;

  return (
    <Card
      withBorder
      w="100%"
      h="100%"
      style={{
        transition: 'all 200ms',
        borderWidth: 2,
        borderColor: isLastActive ? 'var(--mantine-primary-color-filled)' : undefined,
        backgroundColor: isLastActive ? 'var(--mantine-primary-color-light)' : undefined,
        boxShadow: isLastActive ? '0 10px 15px -3px var(--mantine-primary-color-light)' : undefined,
      }}
    >
      <div style={{ paddingBottom: 16, paddingTop: 24, paddingLeft: 24, paddingRight: 24 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <Avatar size="xl" radius="md" src={customer.logo} alt={`${customer.name} logo`} style={{ boxShadow: 'var(--mantine-shadow-md)' }}>
            <Building2 size={48} />
          </Avatar>
          <div style={{ textAlign: 'center' }}>
            <Text size="xl" fw={600}>{customer.name}</Text>
            {isLastActive && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '4px 12px', backgroundColor: 'var(--mantine-primary-color-light)', borderRadius: 9999, marginTop: 8 }}>
                <BooleanIcon value={true} />
                <Text size="sm" fw={500} c="blue">Last Active</Text>
              </div>
            )}
          </div>
        </div>
      </div>

      {shouldShowStats && (
        <div style={{ padding: '0 24px 16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, padding: 16, backgroundColor: 'var(--mantine-color-default-hover)', borderRadius: 'var(--mantine-radius-md)' }}>
            <StatItem
              icon={Building2}
              value={customer.stats?.site_count || 0}
              label="Sites"
              style={{ transition: 'transform 200ms' }}
            />
            <StatItem
              icon={MapPin}
              value={customer.stats?.address_count || 0}
              label="Addresses"
              style={{ transition: 'transform 200ms' }}
            />
            <StatItem
              icon={Layers3}
              value={customer.stats?.floor_count || 0}
              label="Floors"
              style={{ transition: 'transform 200ms' }}
            />
            <StatItem
              icon={Home}
              value={customer.stats?.room_count || 0}
              label="Rooms"
              style={{ transition: 'transform 200ms' }}
            />
            <StatItem
              icon={Users}
              value={customer.stats?.tenant_count || 0}
              label="Tenants"
              style={{ transition: 'transform 200ms' }}
            />
            <StatItem
              icon={UserCircle}
              value={customer.stats?.user_count || 0}
              label="Users"
              style={{ transition: 'transform 200ms' }}
            />
          </div>
        </div>
      )}

      <div style={{ padding: '0 24px 24px' }}>
        <Button
          variant={isLastActive ? "filled" : "outline"}
          fullWidth
          h={44}
          size="sm"
          fw={500}
          onClick={() => onSelect(customer)}
          disabled={isLoading}
        >
          {isLoading ? "Loading..." : isLastActive ? "Resume Session" : "Select Customer"}
        </Button>
      </div>
    </Card>
  );
};

export default CustomerCard;
